import React, { useState, useCallback, useMemo } from "react";
import { Card, Table, Button, Input, Space, Tag, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useLoadList, useQuickSearch } from "../../../hooks";
import { settlementApi } from "../../../api/settlementApi";
import { formatDate, formatDateTime } from "../../../utils/dateUtils";
import { getReplacementId, getReplacementLabel, getVoidReason } from "../utils/revisionDisplay";
import InvoiceListFilter from "./components/InvoiceListFilter";
import InvoiceDetailDrawer from "./components/InvoiceDetailDrawer";
import CollapsibleFilterBar from "../../../components/common/CollapsibleFilterBar";

const InvoiceList = () => {
    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);


    // 加载数据
    const { data, isLoading, refetch } = useLoadList("invoices", settlementApi.invoices.getInvoices, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    // 处理表格分页变化
    const handleTableChange = useCallback((newPagination) => {
        setPagination(newPagination);
    }, []);

    // 处理搜索
    const handleSearch = useCallback((values) => {
        const params = {};
        if (values.invoice_number) params.invoice_number = values.invoice_number;
        if (values.supplier_name) params.supplier_name = values.supplier_name;
        if (values.status && values.status.length > 0) params.status = values.status.join(",");
        if (values.lifecycle_status && values.lifecycle_status.length > 0) params.lifecycle_status = values.lifecycle_status.join(",");
        if (values.dateRange && values.dateRange.length === 2) {
            params.start_date = values.dateRange[0].format("YYYY-MM-DD");
            params.end_date = values.dateRange[1].format("YYYY-MM-DD");
        }
        if (values.has_allocation !== undefined && values.has_allocation !== null) {
            params.has_allocation = values.has_allocation;
        }
        setFilterParams(params);
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    // 重置搜索
    const handleReset = useCallback(() => {
        setFilterParams({});
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    // 快捷搜索
    const quickSearch = useQuickSearch({ setFilterParams, setPagination });

    // 筛选摘要项
    const filterSummaryItems = useMemo(() => {
        const items = [];
        if (filterParams.invoice_number) items.push({ label: "账单号", value: filterParams.invoice_number });
        if (filterParams.supplier_name) items.push({ label: "供应商", value: filterParams.supplier_name });
        if (filterParams.status) {
            const statusMap = { DRAFT: "草稿", PENDING_MATCHING: "待匹配", PARTIALLY_RECONCILED: "部分对账", RECONCILED: "已对账", DISPUTED: "有争议", PAID: "已支付" };
            const statuses = filterParams.status.split(",");
            const labels = statuses.map((s) => statusMap[s] || s);
            items.push({ label: "状态", value: labels.join("/"), fullValue: labels.join(" / ") });
        }
        if (filterParams.lifecycle_status) {
            const lifecycleMap = { ACTIVE: "有效", VOID: "已作废", REPLACED: "已替代" };
            const statuses = filterParams.lifecycle_status.split(",");
            const labels = statuses.map((s) => lifecycleMap[s] || s);
            items.push({ label: "生命周期", value: labels.join("/"), fullValue: labels.join(" / ") });
        }
        if (filterParams.start_date || filterParams.end_date) {
            items.push({ label: "日期", value: `${filterParams.start_date || "..."} ~ ${filterParams.end_date || "..."}` });
        }
        if (filterParams.has_allocation !== undefined) {
            items.push({ label: "分摊", value: filterParams.has_allocation ? "是" : "否" });
        }
        return items;
    }, [filterParams]);

    // 查看详情
    const handleViewDetail = (record) => {
        setSelectedInvoiceId(record.id);
        setDetailDrawerVisible(true);
    };

    const lifecycleConfig = {
        ACTIVE: { color: "success", label: "有效" },
        VOID: { color: "default", label: "已作废" },
        VOIDED: { color: "default", label: "已作废" },
        REPLACED: { color: "orange", label: "已替代" },
    };

    const getFunctionalAmount = (record) => ({
        amount: record.functional_total_amount ?? record.base_total_amount,
        currency: record.functional_currency ?? record.base_currency,
    });

    const columns = [
        {
            title: "账单号",
            dataIndex: "invoice_number",
            key: "invoice_number",
            width: 180,
            render: (text, record) => <a onClick={() => handleViewDetail(record)}>{text}</a>,
        },
        {
            title: "供应商",
            key: "supplier",
            width: 200,
            render: (_, record) => {
                if (record.supplier_name) {
                    return record.supplier_code ? `${record.supplier_name} (${record.supplier_code})` : record.supplier_name;
                }
                return record.supplier_id;
            },
        },
        {
            title: "原币金额",
            key: "original_amount",
            width: 150,
            align: "right",
            render: (_, record) => (
                <span>
                    {Number(record.original_total_amount).toFixed(2)} {record.original_currency}
                </span>
            ),
        },
        {
            title: "本位币金额",
            key: "base_amount",
            width: 150,
            align: "right",
            render: (_, record) => {
                const functionalAmount = getFunctionalAmount(record);
                return (
                    <span>
                        {Number(functionalAmount.amount || 0).toFixed(2)} {functionalAmount.currency}
                    </span>
                );
            },
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => {
                const colorMap = {
                    DRAFT: "default",
                    PENDING_MATCHING: "processing",
                    PARTIALLY_RECONCILED: "warning",
                    RECONCILED: "success",
                    DISPUTED: "error",
                    PAID: "success",
                };
                return <Tag color={colorMap[status]}>{status}</Tag>;
            },
        },
        {
            title: "生命周期",
            dataIndex: "lifecycle_status",
            key: "lifecycle_status",
            width: 180,
            render: (status = "ACTIVE", record) => {
                const normalized = String(status || "ACTIVE").toUpperCase();
                const config = lifecycleConfig[normalized] || { color: "default", label: normalized };
                const voidReason = normalized !== "ACTIVE" ? getVoidReason(record) : null;
                const replacementId = getReplacementId(record);
                const replacementLabel = getReplacementLabel(record);
                const replacementText = replacementLabel && String(replacementLabel) !== String(replacementId) ? `已被 ${replacementLabel} 替代` : "查看替代单";

                return (
                    <Space direction="vertical" size={2}>
                        <Tag color={config.color}>{config.label}</Tag>
                        {voidReason && (
                            <Tooltip title={voidReason}>
                                <span style={{ color: "#8c8c8c", fontSize: 12 }}>原因：{voidReason}</span>
                            </Tooltip>
                        )}
                        {replacementId && (
                            <Button type="link" size="small" onClick={() => handleViewDetail({ id: replacementId })} style={{ padding: 0, height: "auto", fontSize: 12 }}>
                                {replacementText}
                            </Button>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "账单日期",
            dataIndex: "issue_date",
            key: "issue_date",
            width: 120,
            render: (text) => formatDate(text),
        },
        {
            title: "来源",
            dataIndex: "source_type",
            key: "source_type",
            width: 100,
            render: (text) => <Tag>{text}</Tag>,
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 180,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 100,
            render: (_, record) => (
                <Space size="small">
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
                        详情
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title-compact">账单列表</div>
            </div>

            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="账单列表 - 筛选条件"
                quickControls={
                    <Input
                        placeholder="搜索账单号/供应商..."
                        style={{ width: 240, height: 26 }}
                        {...quickSearch.inputProps}
                    />
                }
            >
                <InvoiceListFilter onSearch={handleSearch} onReset={handleReset} autoApply />
            </CollapsibleFilterBar>

            <Card className="data-card">
                <Table
                    size="small"
                    columns={columns}
                    dataSource={data?.items || []}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1500 }}
                />
            </Card>

            <InvoiceDetailDrawer visible={detailDrawerVisible} onClose={() => setDetailDrawerVisible(false)} invoiceId={selectedInvoiceId} />
        </div>
    );
};

export default InvoiceList;
